/* eslint-disable @typescript-eslint/no-unused-vars */
import { ThemeSwitch } from "../../appearance/ThemeSwitch";
import { getThemePreference, themePreferenceToDarkMode } from "../../appearance/user-prefs.service";
import { signOut } from "../../auth/services/signOut";
import { useAuthContext } from "../../auth/state/AuthContext";
import { ReactComponent as ArrowLeft } from "../../shared/assets/arrow-left.svg";
import { ReactComponent as InfoIcon } from "../../shared/assets/informationLogo.svg";
import { api, apiUrl } from "../../shared/services/service";
import { DotIcon } from "../Icons";
import { isDarkModeAtom, themeAtom, userAtom } from "../atoms";
import LoadingWrapper from "../components/LoadingWrapper";
import { toastSuccess } from "../components/ToastContainer";
import { postData } from "../services/service";
import { formatName } from "../services/utils";
import { CustomHeader } from "./CustomHeader";
import { ReminderModal } from "./ReminderModal";
import { ReminderSentModal } from "./ReminderSentModal";
import { useAtomValue, useSetAtom } from "jotai";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { Tooltip } from "react-tooltip";
import { twMerge } from "tailwind-merge";

const Header = () => {
  const auth = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const setUserAtom = useSetAtom(userAtom);
  const setIsDarkMode = useSetAtom(isDarkModeAtom);
  const { imoNumber, vesselCode, vesselName } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRemainderModalOpen, setIsRemainderModalOpen] = useState(false);
  const isActive = location.pathname.includes("/vessels");
  const showTcCode = imoNumber && location.pathname.includes("/voyage") && location.pathname.split("/").length === 3;
  const [latestFormDate, setLatestFormDate] = useState<string | null | undefined>(undefined);
  const [opslinkActive, setOpslinkActive] = useState<boolean>(false);

  const theme = useAtomValue(themeAtom);
  const isDarkMode = useAtomValue(isDarkModeAtom);
  const isDarkModeRef = useRef(isDarkMode);
  const themePreferenceRevisionRef = useRef(0);

  const requestData = useMemo(
    () => ({
      imo_no: imoNumber,
      vessel_name: vesselName,
    }),
    [imoNumber, vesselName],
  );

  const filteredRequestData = Object.fromEntries(
    Object.entries(requestData).filter(([_, value]) => value !== undefined && value !== null),
  ) as Record<string, string>;
  const queryParams = new URLSearchParams(filteredRequestData).toString();
  const navigation = [{ name: "Logout", path: "/", callback: signOut, requiresConfirmation: true }];
  const { firstname = "", lastname = "  ", sub } = auth.state.user?.accessToken || {};
  const formattedFullName = (firstname.length > 0 && `${firstname} ${lastname}`) || "";
  const _userEmail = sub || "";
  const firstNameFirstLetter = firstname?.charAt(0) || "";
  const lastNameFirstLetter = lastname?.charAt(0) || "";

  const user = {
    name: formattedFullName || "",
    email: _userEmail,
    profileImage: "path/to/profile-image.jpg",
    company: {
      name: "Company XYZ",
      logoUrl: "path/to/company-logo.png",
    },
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [resultTechData, resultBalticData] = await Promise.all([
          api.get(`${apiUrl}/td_General_Info`, { params: requestData }),
          api.get(`${apiUrl}/bq_GeneralInfo`, { params: requestData }),
        ]);

        const techFormSubmitted = resultTechData.data[0]?.TECH_FORM_SUBMITTED || null;
        const vesselOpslinkActive = resultTechData.data[0]?.OPSLINK_ACTIVE ?? false;
        const balticFormSubmitted = resultBalticData.data[0]?.BALTIC_FORM_SUBMITTED || null;

        const latestDate = [techFormSubmitted, balticFormSubmitted]
          .filter(Boolean)
          .map((date) => new Date(date))
          .sort((a, b) => b.getTime() - a.getTime())[0];

        if (latestDate) {
          setLatestFormDate(
            latestDate.toLocaleDateString("en-US", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            }),
          );
        } else {
          setLatestFormDate(null);
        }

        setOpslinkActive(vesselOpslinkActive);
      } catch (error: unknown) {}
    };
    if (imoNumber && vesselName) {
      fetchData();
    }
  }, [imoNumber, queryParams, vesselName, requestData]);

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const closeRemainderModal = () => {
    setIsRemainderModalOpen(false);
  };

  const handleSuccess = () => {
    toastSuccess("Your Request has been Sent successfully");
  };

  const confirmTechFormRequest = async () => {
    try {
      const requestData = {
        user_name: formattedFullName,
        vessel_code: vesselCode,
        vessel_name: vesselName,
        vessel_master_email: _userEmail,
        imo_number: imoNumber,
      };

      const filteredRequestData = Object.fromEntries(
        Object.entries(requestData).filter(([_, value]) => value !== undefined && value !== null),
      ) as Record<string, string>;

      const queryParams = new URLSearchParams(filteredRequestData).toString();
      setIsLoading(true);

      const result = await postData(`userTechFormSubmission?${queryParams}`, requestData);

      const output = result.data;

      setIsLoading(false);
      if (output.success) {
        handleSuccess();
      }

      setIsModalOpen(false);
    } catch (error: unknown) {
      setIsLoading(false);
    }
  };

  // TODO we need a better pattern here
  const excludedPaths = [
    "/home",
    "/vessels",
    "/vessel-card",
    "/vessel-data",
    "/hull-cleaning-follower",
    "/hull-paints-info-active-vessels",
    "/sister-vessel-search",
    "/vessel-clustering",
    "/tech-forms-hull-evaluation",
    "/vessel-evaluation-follower",
    "/landingPage",
    "/certificates",
    "/documents",
    "/voyage/assessment",
  ];
  const isBackButtonVisible = !excludedPaths.some((path) => location.pathname.includes(path));

  useEffect(() => {
    const setUser = (name: string, email: string) => {
      if (firstname) {
        setUserAtom({
          name: formatName(name),
          email,
        });
      }
    };
    setUser(formattedFullName, _userEmail);
  }, [formattedFullName, setUserAtom, firstname, _userEmail]);

  useEffect(() => {
    isDarkModeRef.current = isDarkMode;
    themePreferenceRevisionRef.current += 1;
  }, [isDarkMode]);

  useEffect(() => {
    if (!_userEmail) {
      return;
    }

    let cancelled = false;
    const isDarkModeAtRequestStart = isDarkModeRef.current;
    const themePreferenceRevisionAtRequestStart = themePreferenceRevisionRef.current;

    const loadThemePreference = async () => {
      try {
        const themePreference = await getThemePreference();
        if (
          !cancelled &&
          isDarkModeRef.current === isDarkModeAtRequestStart &&
          themePreferenceRevisionRef.current === themePreferenceRevisionAtRequestStart
        ) {
          setIsDarkMode(themePreferenceToDarkMode(themePreference));
        }
      } catch (error) {}
    };

    loadThemePreference();

    return () => {
      cancelled = true;
    };
  }, [_userEmail, setIsDarkMode]);

  return (
    <div className="pt-4">
      <ToastContainer />

      <div className="flex justify-between">
        {/* Back */}
        {isBackButtonVisible ? (
          <button
            onClick={() => navigate(-1)}
            className={twMerge(
              "group ml-6 flex flex-shrink-0 items-center rounded border p-3 text-sm transition-colors focus:outline-none",
              theme.headerComponent.tailwind.buttonBorder,
              theme.headerComponent.tailwind.backButtonBg,
              theme.myVesselsTable.tailwind.linkText,
              theme.headerComponent.tailwind.buttonHoverBg,
              theme.headerComponent.tailwind.buttonHoverText,
            )}
            style={{ width: "90px", height: "36px" }}
          >
            <ArrowLeft className={twMerge("h-5 w-5 fill-current transition-none")} />
            <p className="pl-1">Back</p>
          </button>
        ) : (
          <div className="ml-6 w-[90px]" />
        )}

        <div data-testid="header-user-controls" className="flex items-center">
          <ThemeSwitch userEmail={_userEmail} />
          {auth.state.user?.accessToken.firstname && <CustomHeader user={user} navigation={navigation} />}
        </div>
      </div>

      {/* Vessel Name */}
      {vesselName && (
        <div className="grid grid-flow-col grid-rows-1 gap-0 pt-5">
          <div className="flex justify-between">
            <div className="column">
              <p
                className={twMerge(
                  "mb-2 font-bold capitalize md:mb-0 md:mr-4",
                  theme.headerComponent.tailwind.vesselTitleText,
                  isActive ? "text-xl" : "text-2xl",
                )}
              >
                {vesselName}
              </p>

              <p className={twMerge("flex items-start gap-1 text-xs", theme.headerComponent.tailwind.statusText)}>
                <DotIcon
                  width={12}
                  height={12}
                  style={{ color: `var(${opslinkActive ? "--header-dot-active" : "--header-dot-inactive"})` }}
                />
                {opslinkActive ? "ACTIVE" : "INACTIVE"} - Latest form update: {latestFormDate}
                <InfoIcon
                  className="h-3 w-3"
                  data-tooltip-id="my-tooltip"
                  data-tooltip-html="Refers to data provided by the vessel. IMOS data excluded."
                />
                <Tooltip
                  id="my-tooltip"
                  place="right"
                  disableStyleInjection={true}
                  style={{
                    color: "var(--header-tooltip-text)",
                    background: "var(--header-tooltip-bg)",
                    borderRadius: "8px",
                    position: "absolute",
                    right: "0",
                  }}
                />
              </p>
            </div>
          </div>
        </div>
      )}

      {/* TC Code */}
      {showTcCode && (
        <div className="grid grid-flow-col grid-rows-1 gap-0 pt-6">
          <div className="flex justify-between">
            <div
              className={twMerge(
                "mb-2 font-bold capitalize md:mb-0 md:mr-4",
                theme.headerComponent.tailwind.vesselTitleText,
                isActive ? "text-xl" : "text-2xl",
              )}
            >
              TC: {imoNumber}
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {/* TODO Review if this is correct placement of modals. Seems like they should be in app shell. */}
      <LoadingWrapper
        isLoading={isLoading}
        loadingIndicatorClassName={theme.headerComponent.tailwind.loadingSpinner}
        loadingIndicatorSize="xs"
      >
        <ReminderModal
          isModalOpen={isModalOpen}
          closeRemainderModal={closeRemainderModal}
          closeModal={closeModal}
          firstNameFirstLetter={firstNameFirstLetter}
          lastNameFirstLetter={lastNameFirstLetter}
          fullName={formattedFullName}
          vesselName={vesselName}
          vesselCode={vesselCode}
          confirmTechFormRequest={confirmTechFormRequest}
        />
        <ReminderSentModal
          isModalOpen={isRemainderModalOpen}
          closeRemainderModal={closeRemainderModal}
          firstName={firstNameFirstLetter}
          lastNameFirstLetter={lastNameFirstLetter}
          formattedFullName={formattedFullName}
        />
      </LoadingWrapper>
    </div>
  );
};

export default Header;
